import React, { useState, useContext, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import { drawerSections, drawerItems } from "./items";
import { drawerStyles } from "./styles";
import { useNavigate, useLocation, Outlet, Link } from "react-router-dom";
import {
  AppBar,
  Box,
  CssBaseline,
  IconButton,
  Typography,
  useMediaQuery,
  Avatar,
  Tooltip,
  Badge,
  Chip,
  Stack,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import UserProfileContext from "../../../../context/userProfile/UserProfileContext";
import AuthAPI from "../../../../api/firebase/AuthAPI";
import OrdersAPI from "../../../../api/firebase/OrdersAPI";
import logo from "../../../../assets/images/inproveda-logo.png";

const LeftResponsiveDrawer = (props) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const { window: windowProp } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [placedOrdersCount, setPlacedOrdersCount] = useState(0);
  const location = useLocation();
  const selectedTab =
    drawerItems.find((item) => item.route === location.pathname)?.label ||
    "Dashboard";
  const userProfileContext = useContext(UserProfileContext);
  const currentUser = userProfileContext.state;
  const authAPI = new AuthAPI();
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    if (window.confirm("Are you sure you want to log out of the admin panel?")) {
      try {
        await authAPI.signoutUser();
        userProfileContext.setState(null);
        localStorage.clear();
        navigate("/");
      } catch (ex) {
        console.error("Error signing out:", ex);
      }
    }
  };

  useEffect(() => {
    if (location.pathname === "/admin" || location.pathname === "/admin/") {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    let cancelled = false;

    new OrdersAPI().getOrders()
      .then((result) => {
        if (cancelled || !result?.success || !Array.isArray(result.data)) return;
        setPlacedOrdersCount(result.data.filter((order) => order.status === "Placed").length);
      })
      .catch(() => {
        if (!cancelled) setPlacedOrdersCount(0);
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Brand Header */}
      <Box component={Link} to="/" sx={drawerStyles.brandHeader}>
        <Box sx={drawerStyles.brandLogo}>
          <img src={logo} alt="Inproveda" />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                fontSize: "1rem",
                color: "#ffffff",
                letterSpacing: "-0.01em",
                lineHeight: 1.1,
              }}
            >
              Inproveda
            </Typography>
            <Chip
              label="ADMIN"
              size="small"
              sx={{
                height: 18,
                fontSize: "0.62rem",
                fontWeight: 800,
                bgcolor: "#004d40",
                color: "#34d399",
                border: "1px solid #059669",
                letterSpacing: "0.05em",
                px: 0.2,
              }}
            />
          </Stack>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.55)", fontSize: "0.72rem" }}
          >
            Storefront Console
          </Typography>
        </Box>
      </Box>

      {/* Recategorized Navigation Links */}
      <List sx={drawerStyles.navList}>
        {drawerSections.map((section, sIdx) => (
          <Box key={section.title || sIdx} sx={{ mb: 1.2 }}>
            {section.title && (
              <ListSubheader
                disableSticky
                sx={{
                  backgroundColor: "transparent",
                  color: "rgba(255, 255, 255, 0.45)",
                  fontSize: "0.68rem",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  lineHeight: "24px",
                  px: 1.5,
                  mt: sIdx === 0 ? 0.5 : 1,
                  mb: 0.2,
                }}
              >
                {section.title}
              </ListSubheader>
            )}
            {section.items.map((item) => {
              const isSelected = location.pathname === item.route;
              const isOrdersItem = item.id === "orders";
              return (
                <ListItemButton
                  sx={drawerStyles.listItems}
                  key={item.id}
                  selected={isSelected}
                  aria-current={isSelected ? "page" : undefined}
                  aria-label={isOrdersItem && placedOrdersCount > 0
                    ? `Orders, ${placedOrdersCount} placed ${placedOrdersCount === 1 ? "order" : "orders"}`
                    : undefined}
                  onClick={() => {
                    navigate(item.route);
                    setMobileOpen(false);
                  }}
                >
                  <ListItemIcon sx={drawerStyles.icons}>
                    {isOrdersItem ? (
                      <Badge
                        badgeContent={placedOrdersCount}
                        max={99}
                        invisible={placedOrdersCount === 0}
                        sx={drawerStyles.orderBadge}
                      >
                        {item.icon}
                      </Badge>
                    ) : item.icon}
                  </ListItemIcon>
                  <ListItemText sx={drawerStyles.text} primary={item.label} />
                </ListItemButton>
              );
            })}
          </Box>
        ))}
      </List>

      {/* User Profile & Sign Out Widget */}
      <Box sx={drawerStyles.userFooter}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            variant="dot"
            sx={{
              "& .MuiBadge-badge": {
                width: 8,
                height: 8,
                minWidth: 8,
                padding: 0,
                borderRadius: "50%",
                backgroundColor: "#10b981",
                color: "#10b981",
                boxShadow: "0 0 0 1.5px #02241f",
              },
            }}
          >
            <Avatar
              alt={currentUser?.displayName || "Admin"}
              src={currentUser?.photoURL || "/static/images/avatar/1.jpg"}
              sx={{
                width: 36,
                height: 36,
                bgcolor: "#004d40",
                color: "#ffffff",
                fontWeight: 700,
                fontSize: "0.9rem",
                border: "1.5px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              {(currentUser?.displayName || "A").charAt(0).toUpperCase()}
            </Avatar>
          </Badge>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: "0.85rem",
                color: "#ffffff",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 120,
              }}
            >
              {currentUser?.displayName || "Admin"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.5)",
                fontSize: "0.7rem",
                display: "block",
                lineHeight: 1,
              }}
            >
              Administrator
            </Typography>
          </Box>
        </Stack>

        <Tooltip title="Log Out">
          <IconButton
            size="small"
            onClick={handleLogout}
            sx={{
              color: "rgba(255, 255, 255, 0.65)",
              p: 0.75,
              borderRadius: "8px",
              "&:hover": {
                bgcolor: "rgba(239, 68, 68, 0.15)",
                color: "#f87171",
              },
            }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  const container =
    windowProp !== undefined ? () => windowProp().document.body : undefined;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline enableColorScheme />
      <AppBar position="fixed" sx={drawerStyles.appBar}>
        <Toolbar variant="dense">
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={drawerStyles.appBarIcon}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
            {selectedTab}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={drawerStyles.drawerBox}>
        {isMobile && (
          <Drawer
            container={container}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={drawerStyles.temporaryDrawer}
          >
            {drawer}
          </Drawer>
        )}
        <Drawer
          variant="permanent"
          sx={drawerStyles.permanentDrawer}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={drawerStyles.outletBox}>
        <Toolbar sx={{ display: { sm: "none", xs: "block" } }} />
        <Outlet />
      </Box>
    </Box>
  );
};

export default LeftResponsiveDrawer;
